import * as XLSX from "xlsx";

const REQUIRED_TEMPLATE_FIELDS = ["name", "code", "sections"];
const REQUIRED_SECTION_FIELDS = ["code", "name", "fields"];
const REQUIRED_FIELD_FIELDS = ["code", "label", "inputType"];

const asBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (value === undefined || value === null || value === "") return fallback;
  return ["true", "yes", "y", "1"].includes(String(value).trim().toLowerCase());
};

const parseJson = async (file) => JSON.parse(await file.text());

const readWorkbook = async (file) => {
  const buffer = await file.arrayBuffer();
  return XLSX.read(buffer, { type: "array" });
};

const rowsBySheet = (workbook, sheetName) => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet, { defval: "" });
};

const get = (row, keys) => {
  const found = keys.find((key) => Object.prototype.hasOwnProperty.call(row, key));
  return found ? row[found] : "";
};

const parseOptions = (value) => {
  if (!value) return [];
  return String(value)
    .split("|")
    .map((part, index) => {
      const [label, rawValue] = part.split(":");
      return {
        label: (label || "").trim(),
        value: (rawValue || label || "").trim(),
        sequence: (index + 1) * 10,
        active: true,
      };
    })
    .filter((option) => option.label);
};

const parseExcel = async (file) => {
  const workbook = await readWorkbook(file);
  const templateRow = rowsBySheet(workbook, "Template")[0] || {};
  const sectionRows = rowsBySheet(workbook, "Sections");
  const fieldRows = rowsBySheet(workbook, "Fields");

  const sections = sectionRows.map((row, index) => {
    const sectionCode = String(get(row, ["code", "Code", "Section Code", "sectionCode"])).trim();
    return {
      code: sectionCode,
      name: String(get(row, ["name", "Name", "Section Name"])).trim(),
      description: String(get(row, ["description", "Description"])).trim(),
      sequence: Number(get(row, ["sequence", "Sequence"])) || (index + 1) * 10,
      collapsible: asBoolean(get(row, ["collapsible", "Collapsible"]), true),
      defaultExpanded: asBoolean(get(row, ["defaultExpanded", "Default Expanded"]), true),
      required: asBoolean(get(row, ["required", "Required"])),
      active: true,
      fields: [],
    };
  });

  const sectionMap = new Map(sections.map((section) => [section.code, section]));

  fieldRows.forEach((row, index) => {
    const sectionCode = String(get(row, ["sectionCode", "Section Code", "section", "Section"])).trim();
    const section = sectionMap.get(sectionCode);
    if (!section) return;

    const inputType = String(get(row, ["inputType", "Input Type", "type", "Type"]) || "TEXT")
      .trim()
      .toUpperCase();
    const riskEnabled = asBoolean(get(row, ["riskEnabled", "Risk Enabled"]));

    section.fields.push({
      code: String(get(row, ["code", "Code", "Field Code"])).trim().toUpperCase(),
      label: String(get(row, ["label", "Label", "Field Label"])).trim(),
      description: String(get(row, ["description", "Description"])).trim(),
      inputType,
      dataType: String(get(row, ["dataType", "Data Type"]) || "").trim().toUpperCase(),
      sequence: Number(get(row, ["sequence", "Sequence"])) || (index + 1) * 10,
      required: asBoolean(get(row, ["required", "Required"])),
      readOnly: asBoolean(get(row, ["readOnly", "Read Only"])),
      hidden: asBoolean(get(row, ["hidden", "Hidden"])),
      placeholder: String(get(row, ["placeholder", "Placeholder"])).trim(),
      helpText: String(get(row, ["helpText", "Help Text"])).trim(),
      options: parseOptions(get(row, ["options", "Options"])),
      validations: [],
      rules: [],
      dataSource: { type: String(get(row, ["dataSourceType", "Data Source Type"]) || "MANUAL").trim().toUpperCase() },
      riskConfig: {
        enabled: riskEnabled,
        triggerCode: String(get(row, ["triggerCode", "Trigger Code"])).trim(),
        triggerValue: get(row, ["triggerValue", "Trigger Value"]) || false,
        severity: String(get(row, ["severity", "Severity"]) || "HIGH").trim().toUpperCase(),
        category: String(get(row, ["riskCategory", "Risk Category"])).trim(),
        message: String(get(row, ["riskMessage", "Risk Message"])).trim(),
      },
      calculationConfig: {
        enabled: asBoolean(get(row, ["calculationEnabled", "Calculation Enabled"])),
        expression: String(get(row, ["calculationExpression", "Calculation Expression"])).trim(),
        precision: Number(get(row, ["precision", "Precision"])) || 2,
      },
      uiConfig: { width: Number(get(row, ["width", "Width"])) || (inputType === "TEXTAREA" ? 12 : 6) },
    });
  });

  return {
    name: String(get(templateRow, ["name", "Name"])).trim(),
    code: String(get(templateRow, ["code", "Code"])).trim().toUpperCase(),
    description: String(get(templateRow, ["description", "Description"])).trim(),
    requestType: String(get(templateRow, ["requestType", "Request Type"])).trim(),
    businessUnit: String(get(templateRow, ["businessUnit", "Business Unit"])).trim(),
    division: String(get(templateRow, ["division", "Division"])).trim(),
    country: String(get(templateRow, ["country", "Country"])).trim(),
    sections,
  };
};

const validateTemplate = (template) => {
  const warnings = [];
  const errors = [];
  const fieldCodes = new Set();
  const sectionCodes = new Set();

  REQUIRED_TEMPLATE_FIELDS.forEach((key) => {
    if (!template?.[key] || (Array.isArray(template[key]) && !template[key].length)) {
      errors.push(`Template ${key} is required.`);
    }
  });

  (template?.sections || []).forEach((section) => {
    REQUIRED_SECTION_FIELDS.forEach((key) => {
      if (section[key] === undefined || section[key] === null || section[key] === "") {
        errors.push(`Section ${section.code || section.name || "Unknown"} ${key} is required.`);
      }
    });

    if (sectionCodes.has(section.code)) errors.push(`Section code ${section.code} is duplicated.`);
    sectionCodes.add(section.code);

    (section.fields || []).forEach((field) => {
      REQUIRED_FIELD_FIELDS.forEach((key) => {
        if (!field[key]) errors.push(`Field ${field.code || field.label || "Unknown"} ${key} is required.`);
      });
      if (fieldCodes.has(field.code)) errors.push(`Field code ${field.code} is duplicated.`);
      fieldCodes.add(field.code);
      if (field.riskConfig?.enabled && !field.riskConfig.triggerCode) {
        warnings.push(`${field.code} has risk enabled without a trigger code.`);
      }
      if (["SELECT", "MULTI_SELECT", "RADIO", "QUESTION_SELECT"].includes(field.inputType) && !field.options?.length && field.dataSource?.type === "MANUAL") {
        warnings.push(`${field.code} should define options or a controlled data source.`);
      }
    });
  });

  return { errors, warnings };
};

export const parseRiskReviewTemplateImport = async (file) => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  const template = ext === "json" ? await parseJson(file) : await parseExcel(file);
  const sections = template.sections || [];
  const fields = sections.flatMap((section) => section.fields || []);
  const questions = fields.filter((field) => String(field.inputType).startsWith("QUESTION")).length;
  const riskTriggers = fields.filter((field) => field.riskConfig?.enabled).length;
  const validation = validateTemplate(template);

  return {
    template,
    preview: {
      sections: sections.length,
      fields: fields.length,
      questions,
      riskTriggers,
      warnings: validation.warnings,
      errors: validation.errors,
    },
  };
};

export const toRiskReviewTemplatePayload = (template) => ({
  name: template.name,
  code: template.code,
  description: template.description,
  requestType: template.requestType,
  businessUnit: template.businessUnit,
  division: template.division,
  country: template.country,
  versions: [
    {
      version: 1,
      status: "DRAFT",
      sections: template.sections || [],
      approvalRules: template.approvalRules || [],
    },
  ],
});
